import 'react-table/react-table.css'
import '../../../_components/table/table.styles.css'

import LinkText from '@helvetia/components/LinkText'
import { MatchMedia } from '@helvetia/components/MatchMedia'
import Spinner from '@helvetia/components/Spinner'
import Text from '@helvetia/components/Text'
import dayjs from 'dayjs'
import numeral from 'numeral'
import * as React from 'react'
import { WithNamespaces, withNamespaces } from 'react-i18next'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import ReactTable from 'react-table'

import { LifecycleView } from '../../../_components/ConnectComponent'
import EmailSVG from '../../../_components/icons/email.svg'
import { ClickableIcon } from '../../../_components/icons/icons'
import PdfSVG from '../../../_components/icons/pdf.svg'
import { Modal } from '../../../_components/Modal'
import { offerDocumentViewMapper } from '../OfferView.mapper'
import { PolicyTablePresenter } from './PolicyTable.presenter'

export interface View extends LifecycleView {
  showLoading: () => void
  hideLoading: () => void
}

interface Props {
  presenter: PolicyTablePresenter
}

interface State {
  isLoading: boolean
  isModalOpen: boolean
  page: number
  pages: number
  pageSize: number
  policyList: any[]
}

export class PolicyTableRaw
  extends React.Component<Props & RouteComponentProps & WithNamespaces, State>
  implements View {
  private presenter: PolicyTablePresenter

  constructor(props) {
    super(props)

    this.presenter = props.presenter
    this.presenter.initialize(this)

    this.state = {
      isLoading: false,
      isModalOpen: false,
      page: 0,
      pages: 1,
      pageSize: 20,
      policyList: [],
    }
  }

  setInitialValues = (policyList, pagination) => {
    this.setState({
      policyList: policyList.map(offerDocumentViewMapper),
      page: pagination.currentPage,
      pages: pagination.totalPages,
      pageSize: pagination.itemsPerPage,
    })
  }

  getPolicies = () => {
    this.presenter
      .getPolicies(this.state.page, this.state.pageSize)
      .then(response => {
        this.setState(
          {
            policyList: response.policies.map(offerDocumentViewMapper),
            page: response.pagination.currentPage,
            pages: response.pagination.totalPages,
            pageSize: response.pagination.itemsPerPage,
          },
          this.hideLoading,
        )
      })
  }

  onPageChange = page => {
    this.setState({ isLoading: true, page }, this.getPolicies)
  }

  onPageSizeChange = (pageSize: number) => {
    this.setState({ isLoading: true, pageSize }, this.getPolicies)
  }

  onViewOffer = (offerId: string) => {
    this.presenter.goToViewOffer(offerId)
  }

  showLoading = () => this.setState({ isLoading: true })

  hideLoading = () => this.setState({ isLoading: false })

  showModal = () => this.setState({ isModalOpen: true })

  hideModal = () => this.setState({ isModalOpen: false })

  render() {
    const { isLoading, policyList, isModalOpen } = this.state
    const { t } = this.props

    if (isLoading) return <Spinner size="xl" />

    if (isModalOpen)
      return (
        <Modal
          buttonText={t('buttons.accept')}
          onClose={this.hideModal}
          title={
            <>
              <MatchMedia onlyMobile>
                <Text size="sBold">{t('modal.email.title')}</Text>
              </MatchMedia>
              <MatchMedia tablet>
                <Text size="sBold">{t('modal.email.title')}</Text>
              </MatchMedia>
            </>
          }
        >
          <Text size="m" as="p" color="black">
            {t('modal.email.send')}
          </Text>
        </Modal>
      )

    return (
      <ReactTable
        manual
        resizable={false}
        filterable={true}
        showPagination={true}
        loading={false}
        data={policyList}
        columns={this.columns()}
        onPageChange={page => this.onPageChange(page)}
        onPageSizeChange={pageSize => this.onPageSizeChange(pageSize)}
        page={this.state.page}
        pageSize={this.state.pageSize}
        pages={this.state.pages}
        previousText={t('pagination.previous')}
        nextText={t('pagination.next')}
        pageText={t('pagination.pages')}
        ofText={t('pagination.ofTotal')}
        rowsText={t('pagination.rowsSelectorText')}
      />
    )
  }

  columns() {
    const { t } = this.props
    const { policyList } = this.state

    if (policyList && policyList.length < 1) {
      return []
    }

    return [
      {
        Header: () => <span>ID</span>,
        accessor: 'offerNumber',
        filterable: false,
        width: 80,
        Cell: row => (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-around',
            }}
          >
            <LinkText
              onClick={() => this.onViewOffer(row.original.id)}
              text={row.original.offerNumber}
              color="primary"
            />
          </div>
        ),
      },
      {
        Header: () => t('table.contractDate'),
        accessor: 'validityStartDate',
        // filterable: true,
        filterable: false,
        sorteable: true,
        width: 130,
        Cell: props => dayjs(props.value).format('DD.MM.YYYY'),
      },
      {
        Header: () => t('table.price'),
        accessor: 'price',
        // filterable: true,
        filterable: false,
        sorteable: true,
        width: 130,
        Cell: props => numeral(props.value).format('0,0.00'),
      },
      {
        Header: () => t('table.customer'),
        accessor: 'company',
        // filterable: true,
        filterable: false,
        sorteable: true,
        width: 275,
        Cell: row => (
          <div>
            {row.original.company}
            <br />
            {row.original.firstName + ' ' + row.original.lastName}
          </div>
        ),
      },

      {
        Header: () => t('table.contact'),
        accessor: 'email',
        // filterable: true,
        filterable: false,
        sorteable: true,
        width: 300,
        Cell: row => (
          <div>
            {row.original.email}
            <br />
            {row.original.phone && row.original.phone.trim().length > 3
              ? row.original.phone
              : ''}
          </div>
        ),
      },

      {
        Header: () => t('table.actions'),
        id: 'actionButtons',
        filterable: false,
        width: 400,
        accessor: d => d,
        Cell: row => (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'baseline',
              alignContent: 'center',
              marginLeft: '80px',
              marginTop: '15px',
            }}
          >
            <ClickableIcon
              src={PdfSVG}
              onClick={() =>
                this.presenter.onDownloadOfferPDF(
                  row.original.id,
                  row.original.offerNumber,
                )
              }
              marginRight="l"
            />
            <ClickableIcon
              src={EmailSVG}
              onClick={() =>
                this.presenter.onSendOfferToCustomer(row.original.id)
              }
            />
          </div>
        ),
      },
      /* Filter: ({ onChange }) => (
        <select
          onChange={event => {
            onChange(event.target.value)
            this.props.onRiskTypeSelectorChange(Number(event.target.value))
            this.setState({ isInitialState: false })
          }}
          style={{ width: '100%' }}
          value={
            this.props.riskFilters && this.props.riskFilters.riskTypeId
              ? this.props.riskFilters.riskTypeId
              : 'all'
          }
        >
          {this.printOptionList(this.state.riskTypeList, t)}
        </select>
      ), */
    ]
  }
}

export const PolicyTableView = withNamespaces()(withRouter(PolicyTableRaw))
